import { UIView, UIViewController } from "uikit";
import { BookingProductCategoryViewController, BookingProductCategoryViewControllerDelegate } from "./BookingProductCategoryViewController";
import { BookingProductViewController, BookingProductProtocolDelegate } from "./BookingProductViewController";
import { BookingCalendarViewController } from "./BookingCalendarViewController";
import { BookingClientProtocolDelegate, BookingClientViewController } from "./BookingClientViewController";
import { BookingRestaurantViewController } from "./BookingRestaurantViewController";
import { BookingPaxViewController, BookingPaxProtocolDelegate } from "./BookingPaxViewController";
import { BookingEventViewController, BookingEventProtocolDelegate } from "./BookingEventViewController";
import { BookingViewController } from "./BookingViewController";

/**
 * Root controller of the booking form (desktop and mobile layouts). Each step is created only when
 * its container exists in the venue layout:
 *
 *   pax-form-view              -> BookingPaxViewController       (number of people)
 *   event-form-view            -> BookingEventViewController     (venue events, fixes day + preset)
 *   product-category-form-view -> BookingProductCategoryViewController
 *   product-form-view          -> BookingProductViewController   (tickets / products)
 *   calendar-form-view         -> BookingCalendarViewController  (day, optionally pax)
 *   restaurant-form-view       -> BookingRestaurantViewController(zone + time range)
 *   client-form-view           -> BookingClientViewController
 *
 * Steps are numbered in the order they appear in the layout and share their values through the
 * cart, so any combination/order of containers produces a complete booking.
 */
export class BookingDesktopViewController extends BookingViewController implements BookingProductProtocolDelegate, BookingProductCategoryViewControllerDelegate, BookingClientProtocolDelegate, BookingPaxProtocolDelegate, BookingEventProtocolDelegate
{
    private paxView:UIView;
    private eventView:UIView;
    private productCategoryView:UIView;
    private productView:UIView;
    private calendarView:UIView;
    private restaurantView:UIView;
    private clientView:UIView;

    viewDidLoad(){
        super.viewDidLoad();

        if (this.paxView) {
            this.paxViewController = new BookingPaxViewController();
            this.paxViewController.initWithNibName( "BookingPaxView" );
            this.paxViewController.delegate = this;

            this.paxView.addSubview( this.paxViewController.view );
            this.addChildViewController( this.paxViewController );
        }

        if (this.eventView) {
            this.eventViewController = new BookingEventViewController();
            this.eventViewController.initWithNibName( "BookingEventView" );
            this.eventViewController.delegate = this;

            this.eventView.addSubview( this.eventViewController.view );
            this.addChildViewController( this.eventViewController );
        }

        if (this.productCategoryView) {
            this.productCategoryViewController = new BookingProductCategoryViewController();
            this.productCategoryViewController.initWithNibName( "BookingProductCategoryView" );
            this.productCategoryViewController.delegate = this;

            this.productCategoryView.addSubview( this.productCategoryViewController.view );
            this.addChildViewController( this.productCategoryViewController );
        }

        // Add Booking product view controller
        if (this.productView) {
            this.productViewController = new BookingProductViewController();
            this.productViewController.initWithNibName( "BookingProductView" );
            this.productViewController.delegate = this;

            this.productView.addSubview( this.productViewController.view );
            this.addChildViewController( this.productViewController );
        }

        // Add Booking calendar view controller
        if (this.calendarView) {
            this.calendarViewController = new BookingCalendarViewController();
            this.calendarViewController.initWithNibName( "BookingCalendarView" );
            this.calendarViewController.delegate = this;

            this.calendarView.addSubview( this.calendarViewController.view );
            this.addChildViewController( this.calendarViewController );
        }

        // Add Booking restaurant view controller
        if (this.restaurantView) {
            this.restaurantViewController = new BookingRestaurantViewController();
            this.restaurantViewController.initWithNibName( "BookingRestaurantView" );
            this.restaurantViewController.delegate = this;

            this.restaurantView.addSubview( this.restaurantViewController.view );
            this.addChildViewController( this.restaurantViewController );
        }

        // Add client view
        if (this.clientView) {
            this.clientViewController = new BookingClientViewController();
            this.clientViewController.initWithNibName( "BookingClientView" );
            this.clientViewController.delegate = this;

            this.clientView.addSubview( this.clientViewController.view );
            this.addChildViewController( this.clientViewController );
        }

        this.setupSteps();
    }

    onCalendarDidChange ( vc: BookingCalendarViewController, date:string )
    {
        super.onCalendarDidChange( vc, date );
    }

    onNumPeopleDidChange ( vc: BookingCalendarViewController | BookingPaxViewController, numberOfPeople:number )
    {
        super.onNumPeopleDidChange( vc, numberOfPeople );
    }

    onNumProductDidChange ( vc: BookingProductViewController, numberOfProducts:number ) {
        super.onNumProductDidChange( vc, numberOfProducts );
        // Ticket-style venues (no pax step): the number of tickets is the number of people
        if ( this.paxViewController == null && this.calendarViewController ) this.calendarViewController.setNumPeople( numberOfProducts ) ;
    }

    onEventDidChange ( vc: BookingEventViewController, event: any ) {
        super.onEventDidChange( vc, event );
    }

    categoryDidChange ( vc: BookingProductCategoryViewController, categoryID:string ) {
        this.productViewController?.fetchProducts( categoryID );
    }

    noCategoriesAvailable ( vc: BookingProductCategoryViewController ) {
        this.productViewController?.fetchAllProducts();
    }

    onClientFormDidChange ( vc: BookingClientViewController ) {
        super.onClientFormDidChange( vc );
    }
}
